import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import SelectAssignee from 'flow/components/tickets/Assignee/SelectAssignee';
import TicketAssigneeItem from 'flow/components/tickets/TicketAssigneeItem';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectAssignee, selectMembers } from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { getTicketAssigneeOptionsFromMembers } from 'flow/utils/ticket-utils';
import useTicketAction from 'flow/hooks/useTicketAction';

interface Props {
    ticketKey: TicketKey;
    contextForSegment: string;
}

const AssigneeOptionsItem = ({ ticketKey, contextForSegment }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const assigneeKey = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const canAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );
    const members = useAppSelector((state) => selectMembers(state, ticketKey));
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    const assigneeOptions = useMemo(() => {
        return getTicketAssigneeOptionsFromMembers(
            i18next,
            ownExtensionKey,
            members,
            extensions,
        );
    }, [i18next, ownExtensionKey, members, extensions]);

    const [
        isPerformingSetAssigneeAPIRequest,
        setIsPerformingSetAssigneeAPIRequest,
    ] = useState(false);

    const [keyToDisplayDuringPostRequest, setKeyToDisplayDuringPostRequest] =
        useState(undefined);

    const onAssigneeSelected = (
        assigneeSelected: ExtensionKey | 'unassigned',
    ) => {
        setKeyToDisplayDuringPostRequest(assigneeSelected);
        setIsPerformingSetAssigneeAPIRequest(true);

        dispatch(
            segmentTrack(TICKET_STRINGS.USER_ASSIGNED_TICKET, {
                component: contextForSegment,
            }),
        );

        runTicketAction(updateTicketAssignee(ticketKey, assigneeSelected))
            .catch((error: any) => {
                if (error.status === 404) {
                    openModal('confirmationDialog', {
                        title: i18next.t('tickets.eligible.title'),
                        description: i18next.t('tickets.eligible.ccpro'),
                        hideCancelButton: true,
                        handleAccept: () => {},
                    });
                }
            })
            .finally(() => {
                setIsPerformingSetAssigneeAPIRequest(false);
                setKeyToDisplayDuringPostRequest(undefined);
            });
    };

    return (
        <div>
            <div className="mb-1.5 text-xs font-bold">
                {i18next.t('ticket.assignee')}
            </div>
            <SelectAssignee
                options={assigneeOptions}
                title={
                    <TicketAssigneeItem
                        assigneeKey={
                            keyToDisplayDuringPostRequest || assigneeKey
                        }
                    />
                }
                onItemSelected={onAssigneeSelected}
                disabled={!canAssign || isPerformingSetAssigneeAPIRequest}
            />
        </div>
    );
};

export default AssigneeOptionsItem;
